import * as THREE from 'three';

const EPS = 1e-6;

/**
 * 读取特征结果体的面三角范围。
 *
 * 设计说明：
 * 1. 统一通过结果体对象公开方法读取面三角范围；
 * 2. 宿主不再依赖任何隐式字段；
 * 3. 若对象未实现该方法，则视为不支持按面解析。
 *
 * @param mesh 目标网格。
 * @returns 面三角范围。
 */
function readFaceTriangleRanges(mesh: THREE.Mesh): [number, number][] | undefined {
  const getFaceTriangleRanges = (mesh as THREE.Mesh & {
    getFaceTriangleRanges?: () => [number, number][] | undefined;
  }).getFaceTriangleRanges;
  if (typeof getFaceTriangleRanges === 'function') {
    return getFaceTriangleRanges.call(mesh);
  }
  return undefined;
}

/**
 * 判断值是否为三维向量元组。
 *
 * @param value 待判断值。
 * @returns 是否有效。
 */
export function isVector3(value: unknown): value is [number, number, number] {
  return Array.isArray(value) && value.length === 3 && value.every((item) => typeof item === 'number');
}

/**
 * 判断值是否为三维点列。
 *
 * @param value 待判断值。
 * @param minLength 最小长度。
 * @returns 是否有效。
 */
export function isPointList(value: unknown, minLength: number): value is Array<[number, number, number]> {
  return Array.isArray(value) && value.length >= minLength && value.every(isVector3);
}

/**
 * 转换为 THREE.Vector3。
 *
 * @param value 三维元组。
 * @returns 向量对象。
 */
export function toVector3(value: [number, number, number]): THREE.Vector3 {
  return new THREE.Vector3(value[0], value[1], value[2]);
}

/**
 * 归一化向量。
 *
 * @param value 输入向量。
 * @returns 单位向量；退化时回退到 Z 轴。
 */
export function normalizeVector(value: THREE.Vector3): THREE.Vector3 {
  if (value.lengthSq() < EPS) {
    return new THREE.Vector3(0, 0, 1);
  }
  return value.normalize();
}

/**
 * 解析实体面朝外法向。
 *
 * @param faceCenter 面中心。
 * @param faceNormal 面法向。
 * @param solidCenter 实体中心。
 * @returns 朝外法向。
 */
export function resolveOutwardNormalForSolidFace(
  faceCenter: THREE.Vector3,
  faceNormal: THREE.Vector3,
  solidCenter: THREE.Vector3,
): THREE.Vector3 {
  const normalized = normalizeVector(faceNormal.clone());
  const toFace = faceCenter.clone().sub(solidCenter);
  if (toFace.lengthSq() < EPS) {
    return normalized;
  }
  if (normalized.dot(toFace) < 0) {
    normalized.negate();
  }
  return normalized;
}

/**
 * 构造局部平面轴。
 *
 * @param normal 法向。
 * @returns 局部 X/Y 轴。
 */
export function buildAxes(normal: THREE.Vector3): {xAxis: THREE.Vector3; yAxis: THREE.Vector3} {
  const up = Math.abs(normal.dot(new THREE.Vector3(0, 1, 0))) > 0.9
    ? new THREE.Vector3(0, 0, 1)
    : new THREE.Vector3(0, 1, 0);
  const xAxis = new THREE.Vector3().crossVectors(normal, up).normalize();
  const yAxis = new THREE.Vector3().crossVectors(normal, xAxis).normalize();
  return {xAxis, yAxis};
}

/**
 * 计算点集中心。
 *
 * @param points 点列。
 * @returns 中心点。
 */
export function computeCenter(points: Array<[number, number, number]>): THREE.Vector3 {
  const center = new THREE.Vector3();
  points.forEach((point) => {
    center.add(toVector3(point));
  });
  return center.divideScalar(points.length || 1);
}

/**
 * 读取几何局部坐标轴。
 *
 * @param geometry 几何载荷。
 * @param fallbackNormal 回退法向。
 * @returns 法向与局部轴。
 */
export function readAxes(
  geometry: {normal?: [number, number, number]; xAxis?: [number, number, number]; yAxis?: [number, number, number]},
  fallbackNormal: THREE.Vector3,
): {normal: THREE.Vector3; xAxis: THREE.Vector3; yAxis: THREE.Vector3} {
  const normal = geometry.normal ? normalizeVector(toVector3(geometry.normal)) : normalizeVector(fallbackNormal.clone());
  const xAxis = geometry.xAxis ? toVector3(geometry.xAxis).normalize() : buildAxes(normal).xAxis;
  const yAxis = geometry.yAxis ? toVector3(geometry.yAxis).normalize() : buildAxes(normal).yAxis;
  return {normal, xAxis, yAxis};
}

/**
 * 计算指定 mesh 面的中心与法向。
 *
 * @param mesh 目标网格。
 * @param faceIndex 面索引。
 * @returns 面中心与法向；失败时返回 null。
 */
export function computeFaceData(mesh: THREE.Mesh, faceIndex: number): {center: THREE.Vector3; normal: THREE.Vector3} | null {
  const ranges = readFaceTriangleRanges(mesh);
  if (!ranges || !ranges[faceIndex]) {
    return null;
  }

  const [start, end] = ranges[faceIndex];
  const geometry = mesh.geometry as THREE.BufferGeometry;
  const positions = geometry.attributes.position?.array as Float32Array | undefined;
  if (!positions) {
    return null;
  }

  mesh.updateMatrixWorld();
  const indices = geometry.index?.array as Uint32Array | undefined;
  const center = new THREE.Vector3();
  const normal = new THREE.Vector3();
  let vertexCount = 0;

  for (let triangleIndex = start; triangleIndex < end; triangleIndex += 1) {
    const base = triangleIndex * 3;
    const index0 = indices ? indices[base] : base;
    const index1 = indices ? indices[base + 1] : base + 1;
    const index2 = indices ? indices[base + 2] : base + 2;

    const point0 = new THREE.Vector3(
      positions[index0 * 3],
      positions[index0 * 3 + 1],
      positions[index0 * 3 + 2],
    ).applyMatrix4(mesh.matrixWorld);
    const point1 = new THREE.Vector3(
      positions[index1 * 3],
      positions[index1 * 3 + 1],
      positions[index1 * 3 + 2],
    ).applyMatrix4(mesh.matrixWorld);
    const point2 = new THREE.Vector3(
      positions[index2 * 3],
      positions[index2 * 3 + 1],
      positions[index2 * 3 + 2],
    ).applyMatrix4(mesh.matrixWorld);

    center.add(point0).add(point1).add(point2);
    vertexCount += 3;

    const triangleNormal = new THREE.Vector3().subVectors(point1, point0).cross(new THREE.Vector3().subVectors(point2, point0));
    if (triangleNormal.lengthSq() > EPS) {
      normal.add(triangleNormal.normalize());
    }
  }

  if (vertexCount === 0) {
    return null;
  }

  center.divideScalar(vertexCount);
  if (normal.lengthSq() > EPS) {
    normal.normalize();
  }

  return {center, normal};
}

/**
 * 判断指定面是否近似共面。
 *
 * @param mesh 目标网格。
 * @param faceIndex 面索引。
 * @param tolerance 法向点积容差。
 * @returns 是否共面。
 */
export function isFacePlanar(mesh: THREE.Mesh, faceIndex: number, tolerance = 0.995): boolean {
  const ranges = readFaceTriangleRanges(mesh);
  if (!ranges || !ranges[faceIndex]) {
    return false;
  }

  const [start, end] = ranges[faceIndex];
  const geometry = mesh.geometry as THREE.BufferGeometry;
  const positions = geometry.attributes.position?.array as Float32Array | undefined;
  if (!positions) {
    return false;
  }

  const indices = geometry.index?.array as Uint32Array | Uint16Array | undefined;
  let referenceNormal: THREE.Vector3 | null = null;

  for (let triangleIndex = start; triangleIndex < end; triangleIndex += 1) {
    const base = triangleIndex * 3;
    const index0 = indices ? indices[base] : base;
    const index1 = indices ? indices[base + 1] : base + 1;
    const index2 = indices ? indices[base + 2] : base + 2;

    const point0 = new THREE.Vector3(
      positions[index0 * 3],
      positions[index0 * 3 + 1],
      positions[index0 * 3 + 2],
    );
    const point1 = new THREE.Vector3(
      positions[index1 * 3],
      positions[index1 * 3 + 1],
      positions[index1 * 3 + 2],
    );
    const point2 = new THREE.Vector3(
      positions[index2 * 3],
      positions[index2 * 3 + 1],
      positions[index2 * 3 + 2],
    );

    const triangleNormal = new THREE.Vector3().subVectors(point1, point0).cross(new THREE.Vector3().subVectors(point2, point0));
    if (triangleNormal.lengthSq() < EPS) {
      continue;
    }
    triangleNormal.normalize();

    if (!referenceNormal) {
      referenceNormal = triangleNormal;
      continue;
    }

    if (Math.abs(triangleNormal.dot(referenceNormal)) < tolerance) {
      return false;
    }
  }

  return referenceNormal !== null;
}
